import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SeniorFlowService } from 'src/services/api/SeniorFlowService';

// `scope` is either `{ token }` (magic link) or `{ secMappingId }` (opened
// from the authenticated "My Applications" list) — every hook below picks
// the matching protected/public service call from it.
const isProtected = (scope) => scope?.secMappingId != null;
const scopeKey = (scope) =>
  isProtected(scope) ? `pid:${scope.secMappingId}` : scope?.token;
const landingKey = (scope) => ['seniorFlow', scopeKey(scope)];

/**
 * Landing info for the Senior Level Flow link. Drives which stage the page shows.
 * Disabled without a token/secMappingId; not retried so an invalid/expired
 * link (or a pipeline that isn't the candidate's) surfaces its terminal panel
 * immediately.
 */
export function useSeniorFlowLanding(scope = {}) {
  return useQuery({
    queryKey: landingKey(scope),
    queryFn: () =>
      isProtected(scope)
        ? SeniorFlowService.getLandingProtected(scope.secMappingId)
        : SeniorFlowService.getLanding(scope.token),
    enabled: !!(scope.token || scope.secMappingId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

// Every mutation re-fetches the landing on success (the flow may have advanced;
// the doc says re-render from a fresh landing rather than derive state locally).
const invalidateLanding = (queryClient, scope) =>
  queryClient.invalidateQueries({ queryKey: landingKey(scope) });

/** Acknowledge / decline the outreach (accepted: boolean). */
export function useAcknowledgeOutreach(scope = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accepted) =>
      isProtected(scope)
        ? SeniorFlowService.acknowledgeProtected(scope.secMappingId, accepted)
        : SeniorFlowService.acknowledge(scope.token, accepted),
    onSuccess: () => invalidateLanding(queryClient, scope),
  });
}

/** PATCH a flow-scoped profile section ({ key, data }). */
export function useUpdateSeniorFlowProfile(scope = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, data }) =>
      isProtected(scope)
        ? SeniorFlowService.updateProfileSectionProtected(
            scope.secMappingId,
            key,
            data
          )
        : SeniorFlowService.updateProfileSection(scope.token, key, data),
    onSuccess: () => invalidateLanding(queryClient, scope),
  });
}

// Accept/decline for the interview *embedded in this same flow* (the
// `interview`/`interviewDone` stages below) has no protected mirror yet —
// the protected "My Applications" list sends interviews through the
// separate InterviewInvite flow instead. These stay token-only; called with
// `token: undefined` in protected mode they're simply inert, which matches
// the protected landing payload never populating that stage.
/** Accept the scheduled interview. */
export function useAcceptInterview(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => SeniorFlowService.acceptInterview(token),
    onSuccess: () => invalidateLanding(queryClient, { token }),
  });
}

/** Decline the scheduled interview. */
export function useDeclineInterview(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => SeniorFlowService.declineInterview(token),
    onSuccess: () => invalidateLanding(queryClient, { token }),
  });
}

const skillMatchKey = (scope) => ['skillMatch', scopeKey(scope)];

/**
 * The job-derived skill requirement list, with the candidate's existing
 * answers. On a magic link, `scope.landingSecMappingId` — read off the
 * already-fetched landing response — is preferred for the URL path; the
 * service only decodes it from the token itself when that's missing.
 */
export function useSkillMatch(scope = {}) {
  return useQuery({
    queryKey: skillMatchKey(scope),
    queryFn: () =>
      isProtected(scope)
        ? SeniorFlowService.getSkillMatchProtected(scope.secMappingId)
        : SeniorFlowService.getSkillMatch(
            scope.token,
            scope.landingSecMappingId
          ),
    enabled: !!(scope.token || scope.secMappingId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** PATCH the skills the candidate confirmed having ({ skills: [...] }). */
export function useUpdateSkillMatch(scope = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      isProtected(scope)
        ? SeniorFlowService.updateSkillMatchProtected(
            scope.secMappingId,
            payload
          )
        : SeniorFlowService.updateSkillMatch(
            scope.token,
            payload,
            scope.landingSecMappingId
          ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: skillMatchKey(scope) }),
  });
}
